(() => {
  /* =======================================================
     AURA REVIEWS CAROUSEL

     aura-reviews-feed.js owns:
     - fetching reviews
     - creating cards
     - customer photos
     - stars
     - reviewer information
     - product information
     - click-to-open behavior
     - full review modal

     This file ONLY controls carousel navigation.
     ======================================================= */


  class AuraReviewsCarousel {

    constructor(root) {

      this.root =
        root;


      this.track =
        root.querySelector(
          '[data-aura-reviews-track]'
        );


      this.controls =
        root.querySelector(
          '[data-aura-reviews-controls]'
        );


      this.prevButton =
        root.querySelector(
          '[data-aura-reviews-prev]'
        );


      this.nextButton =
        root.querySelector(
          '[data-aura-reviews-next]'
        );


      if (
        !this.track
      ) {
        return;
      }


      this.bindEvents();

      this.observeCards();

      this.updateControls();
    }


    /* =====================================================
       EVENTS
       ===================================================== */

    bindEvents() {

      this.prevButton
        ?.addEventListener(
          'click',
          () => {
            this.scrollCards(
              -1
            );
          }
        );


      this.nextButton
        ?.addEventListener(
          'click',
          () => {
            this.scrollCards(
              1
            );
          }
        );


      this.track
        .addEventListener(
          'scroll',
          () => {
            this.updateControls();
          },
          {
            passive: true
          }
        );


      window
        .addEventListener(
          'resize',
          () => {
            this.updateControls();
          },
          {
            passive: true
          }
        );
    }


    /* =====================================================
       WATCH EXISTING REVIEW FEED

       aura-reviews-feed.js inserts the existing cards.

       This observer simply notices when they arrive.
       ===================================================== */

    observeCards() {

      const observer =
        new MutationObserver(
          () => {
            this.updateControls();
          }
        );


      observer.observe(
        this.track,
        {
          childList: true
        }
      );
    }


    /* =====================================================
       CARD STEP
       ===================================================== */

    getStep() {

      const card =
        this.track
          .querySelector(
            '.aura-api-review'
          );


      if (
        !card
      ) {
        return 0;
      }


      const styles =
        window
          .getComputedStyle(
            this.track
          );


      const gap =
        parseFloat(
          styles.columnGap ||
          styles.gap ||
          '0'
        ) ||
        0;


      const cardWidth =
        card
          .getBoundingClientRect()
          .width;


      return (
        cardWidth +
        gap
      );
    }


    /* =====================================================
       SCROLL
       ===================================================== */

    scrollCards(
      direction
    ) {

      const step =
        this.getStep();


      if (
        !step
      ) {
        return;
      }


      this.track.scrollBy({
        left:
          direction *
          step,

        behavior:
          'smooth'
      });
    }


    /* =====================================================
       CONTROLS
       ===================================================== */

    updateControls() {

      const cards =
        this.track
          .querySelectorAll(
            '.aura-api-review:not(.aura-api-review--skeleton)'
          );


      const maxScroll =
        Math.max(
          0,

          this.track.scrollWidth -
          this.track.clientWidth
        );


      const canScroll =
        cards.length > 0 &&
        maxScroll > 2;


      if (
        this.controls
      ) {
        this.controls.hidden =
          !canScroll;
      }


      if (
        !this.prevButton ||
        !this.nextButton
      ) {
        return;
      }


      this.prevButton.disabled =
        !canScroll ||
        this.track.scrollLeft <=
          2;


      this.nextButton.disabled =
        !canScroll ||
        this.track.scrollLeft >=
          maxScroll -
          2;
    }
  }


  /* =======================================================
     INITIALIZE
     ======================================================= */

  function init() {

    document
      .querySelectorAll(
        '[data-aura-reviews-carousel]'
      )
      .forEach(
        root => {

          if (
            root
              .dataset
              .auraReviewsCarouselReady ===
            'true'
          ) {
            return;
          }


          root
            .dataset
            .auraReviewsCarouselReady =
            'true';


          new AuraReviewsCarousel(
            root
          );
        }
      );
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init
    );

  } else {

    init();
  }


  document.addEventListener(
    'shopify:section:load',
    event => {

      const root =
        event.target.matches?.(
          '[data-aura-reviews-carousel]'
        )
          ? event.target

          : event.target
              .querySelector?.(
                '[data-aura-reviews-carousel]'
              );


      if (
        root
      ) {
        delete (
          root
            .dataset
            .auraReviewsCarouselReady
        );
      }


      init();
    }
  );

})();